import Vue from "vue";
import { defineStore } from "pinia";
import { getConnectionId, useConnectionStore } from "@/stores/workflowConnectionStore";
import { assertDefined } from "@/utils/assertions";
export const useWorkflowStepStore = defineStore("workflowStepStore", {
    state: () => ({
        steps: {},
        stepMapOver: {},
        stepInputMapOver: {},
        stepIndex: -1,
        stepExtraInputs: {},
    }),
    getters: {
        getStep(state) {
            return (stepId) => {
                return state.steps[stepId.toString()];
            };
        },
        getStepExtraInputs(state) {
            return (stepId) => this.stepExtraInputs[stepId] || [];
        },
        getStepIndex(state) {
            return Math.max(...Object.values(state.steps).map((step) => step.id), state.stepIndex);
        },
        hasActiveOutputs(state) {
            return Boolean(Object.values(state.steps).find((step) => { var _a; return (_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.length; }));
        },
        workflowOutputs(state) {
            const workflowOutputs = {};
            Object.values(state.steps).forEach((step) => {
                var _a;
                if ((_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.length) {
                    step.workflow_outputs.forEach((workflowOutput) => {
                        if (workflowOutput.label) {
                            workflowOutputs[workflowOutput.label] = {
                                outputName: workflowOutput.output_name,
                                stepId: step.id,
                            };
                        }
                    });
                }
            });
            return workflowOutputs;
        },
        duplicateLabels(state) {
            const duplicateLabels = new Set();
            const labels = new Set();
            Object.values(state.steps).forEach((step) => {
                var _a;
                if ((_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.length) {
                    step.workflow_outputs.forEach((workflowOutput) => {
                        if (workflowOutput.label) {
                            if (labels.has(workflowOutput.label)) {
                                duplicateLabels.add(workflowOutput.label);
                            }
                            labels.add(workflowOutput.label);
                        }
                    });
                }
            });
            return duplicateLabels;
        },
    },
    actions: {
        addStep(newStep) {
            const stepId = newStep.id ? newStep.id : this.getStepIndex + 1;
            const step = Object.freeze(Object.assign(Object.assign({}, newStep), { id: stepId }));
            Vue.set(this.steps, stepId.toString(), step);
            const connectionStore = useConnectionStore();
            stepToConnections(step).map((connection) => connectionStore.addConnection(connection));
            this.stepExtraInputs[step.id] = getStepExtraInputs(step);
            return step;
        },
        insertNewStep(contentId, name, type, position) {
            const stepData = {
                name: name,
                content_id: contentId,
                input_connections: {},
                type: type,
                inputs: [],
                outputs: [],
                position: position,
                post_job_actions: {},
                tool_state: {},
            };
            return this.addStep(stepData);
        },
        updateStep(step) {
            var _a;
            const workflow_outputs = (_a = step.workflow_outputs) === null || _a === void 0 ? void 0 : _a.filter((workflowOutput) => step.outputs.find((output) => workflowOutput.output_name == output.name));
            this.steps[step.id.toString()] = Object.freeze(Object.assign(Object.assign({}, step), { workflow_outputs }));
            this.stepExtraInputs[step.id] = getStepExtraInputs(step);
        },
        changeStepMapOver(stepId, mapOver) {
            Vue.set(this.stepMapOver, stepId, mapOver);
        },
        resetStepInputMapOver(stepId) {
            Vue.set(this.stepInputMapOver, stepId, {});
        },
        changeStepInputMapOver(stepId, inputName, mapOver) {
            if (this.stepInputMapOver[stepId]) {
                Vue.set(this.stepInputMapOver[stepId], inputName, mapOver);
            }
            else {
                Vue.set(this.stepInputMapOver, stepId, { [inputName]: mapOver });
            }
        },
        addConnection(connection) {
            const inputStep = this.getStep(connection.input.stepId);
            assertDefined(inputStep, `Failed to add connection, because step with id ${connection.input.stepId} is undefined`);
            const input = inputStep.inputs.find((input) => input.name === connection.input.name);
            const connectionLink = {
                output_name: connection.output.name,
                id: connection.output.stepId,
            };
            if (input && "input_subworkflow_step_id" in input && input.input_subworkflow_step_id !== undefined) {
                connectionLink["input_subworkflow_step_id"] = input.input_subworkflow_step_id;
            }
            let connectionLinks = [connectionLink];
            let inputConnection = inputStep.input_connections[connection.input.name];
            if (inputConnection) {
                if (!Array.isArray(inputConnection)) {
                    inputConnection = [inputConnection];
                }
                inputConnection = inputConnection.filter((connection) => !(connection.id === connectionLink.id && connection.output_name === connectionLink.output_name));
                connectionLinks = [...connectionLinks, ...inputConnection];
            }
            const updatedStep = Object.assign(Object.assign({}, inputStep), { input_connections: Object.assign(Object.assign({}, inputStep.input_connections), { [connection.input.name]: connectionLinks.sort((a, b) => a.id === b.id ? a.output_name.localeCompare(b.output_name) : a.id - b.id) }) });
            this.updateStep(updatedStep);
        },
        removeConnection(connection) {
            const inputStep = this.getStep(connection.input.stepId);
            assertDefined(inputStep, `Failed to remove connection, because step with id ${connection.input.stepId} is undefined`);
            const inputConnections = inputStep.input_connections[connection.input.name];
            if (this.getStepExtraInputs(inputStep.id).find((input) => connection.input.name === input.name)) {
                inputStep.input_connections[connection.input.name] = undefined;
            }
            else {
                if (Array.isArray(inputConnections)) {
                    inputStep.input_connections[connection.input.name] = inputConnections.filter((outputLink) => !(outputLink.id === connection.output.stepId,
                        outputLink.output_name === connection.output.name));
                }
                else {
                    Vue.delete(inputStep.input_connections, connection.input.name);
                }
            }
            this.updateStep(inputStep);
        },
        removeStep(stepId) {
            const connectionStore = useConnectionStore();
            connectionStore
                .getConnectionsForStep(stepId)
                .forEach((connection) => connectionStore.removeConnection(getConnectionId(connection)));
            Vue.delete(this.steps, stepId.toString());
            Vue.delete(this.stepExtraInputs, stepId);
        },
    },
});
export function stepToConnections(step) {
    const connections = [];
    if (step.input_connections) {
        Object.entries(step === null || step === void 0 ? void 0 : step.input_connections).forEach(([inputName, outputArray]) => {
            if (outputArray === undefined) {
                return;
            }
            if (!Array.isArray(outputArray)) {
                outputArray = [outputArray];
            }
            outputArray.forEach((output) => {
                const connection = {
                    input: {
                        stepId: step.id,
                        name: inputName,
                        connectorType: "input",
                    },
                    output: {
                        stepId: output.id,
                        name: output.output_name,
                        connectorType: "output",
                    },
                };
                const connectionInput = step.inputs.find((input) => input.name == inputName);
                if (connectionInput && "input_subworkflow_step_id" in connectionInput) {
                    connection.input.input_subworkflow_step_id = connectionInput.input_subworkflow_step_id;
                }
                connections.push(connection);
            });
        });
    }
    return connections;
}
function getStepExtraInputs(step) {
    const extraInputs = [];
    if (step.when !== undefined) {
        Object.keys(step.input_connections).forEach((inputName) => {
            var _a;
            if (!step.inputs.find((input) => input.name === inputName) && ((_a = step.when) === null || _a === void 0 ? void 0 : _a.includes(inputName))) {
                const terminalSource = {
                    name: inputName,
                    optional: false,
                    input_type: "parameter",
                    type: "boolean",
                    multiple: false,
                    label: inputName,
                    extensions: [],
                };
                extraInputs.push(terminalSource);
            }
        });
    }
    return extraInputs;
}
